import {useState} from '@wordpress/element';
import {Elements, PaymentElement} from "@stripe/react-stripe-js";
import {StoreNotice} from '@woocommerce/blocks-checkout';
import {getSetting} from '@woocommerce/settings';
import {initStripe as loadStripe, cartContainsSubscription, cartContainsPreOrder} from '../util'
import {useCreatePaymentMethod, useProcessCheckoutError, useProcessCheckoutSuccess} from "../hooks";
import {MandateText} from "../../components/checkout";

/**
 * Return true if the local payment method can be used.
 * @param settings
 * @returns {function({billingAddress: *, [p: string]: *}): *}
 */
export const canMakePayment = (settings, callback = false) => ({billingAddress, cartTotals, ...props}) => {
    const {currency_code} = cartTotals;
    const {country} = billingAddress;
    const countries = settings('countries');
    const type = settings('allowedCountries');
    const supports = settings('features');
    let canMakePayment = false;
    if (settings('isAdmin')) {
        canMakePayment = true;
    } else {
        // Check if there are any subscriptions or pre-orders in the cart.
        if (cartContainsSubscription() && !supports.includes('subscriptions')) {
            return false;
        } else if (cartContainsPreOrder() && !supports.includes('pre-orders')) {
            return false;
        }
        if (settings('currencies').includes(currency_code)) {
            if (type === 'all_except') {
                canMakePayment = !settings('exceptCountries').includes(country);
            } else if (type === 'specific') {
                canMakePayment = settings('specificCountries').includes(country);
            } else {
                canMakePayment = countries.length > 0 ? countries.includes(country) : true;
            }
        }
        if (callback && canMakePayment) {
            canMakePayment = callback({settings, billingAddress, cartTotals, ...props});
        }
    }
    return canMakePayment;
}

export const LocalPaymentIntentContent = (props) => {
    const {getData, billing, cartData, mandate = false, mandateText = ''} = props;
    const name = getData('name');
    const {extensions} = cartData;
    const {cartTotal, currency} = billing;

    let ELEMENT_OPTIONS = {
        mode: 'payment',
        currency: currency?.code?.toLowerCase(),
        ...extensions[name]?.elementOptions
    }

    if (cartTotal.value <= 0) {
        ELEMENT_OPTIONS.mode = 'setup';
    }

    if (['payment', 'subscription'].includes(ELEMENT_OPTIONS.mode)) {
        ELEMENT_OPTIONS.amount = cartTotal.value;
    }

    return (
        <Elements stripe={loadStripe} options={ELEMENT_OPTIONS}>
            <PaymentMethodContent {...props}/>
            {mandate && <MandateText text={mandateText}/>}
        </Elements>
    )
}

const PaymentMethodContent = (
    {
        getData,
        billing,
        emitResponse,
        shouldSavePayment,
        eventRegistration,
        activePaymentMethod,
        shouldCreatePaymentMethod = true
    }) => {
    const name = getData('name');
    const {billingAddress} = billing;
    const {onCheckoutSuccess, onCheckoutFail} = eventRegistration;
    const [loadError, setLoadError] = useState(false);

    const PAYMENT_ELEMENT_OPTIONS = {
        defaultValues: {
            billingDetails: {
                phone: billingAddress.phone,
                email: billingAddress.email,
                name: `${billingAddress.first_name} ${billingAddress.last_name}`,
                address: {
                    country: billingAddress.country,
                    state: billingAddress.state
                }
            }
        },
        fields: {
            billingDetails: {address: 'never', name: 'never', email: 'never'}
        },
        wallets: {applePay: 'never', googlePay: 'never'},
        ...getData('paymentElementOptions')
    }


    useProcessCheckoutError({
        emitResponse,
        subscriber: onCheckoutFail,
        messageContext: emitResponse.noticeContexts.PAYMENTS
    });

    useCreatePaymentMethod({
        name,
        emitResponse,
        billingAddress,
        shouldSavePayment,
        eventRegistration,
        activePaymentMethod,
        shouldCreatePaymentMethod
    });

    useProcessCheckoutSuccess({
        name,
        emitResponse,
        billingAddress,
        onCheckoutSuccess,
        activePaymentMethod,
    });

    const onChange = (event) => {
    };

    const onLoadError = response => {
        const {error} = response;
        setLoadError(error);
    }

    if (loadError) {
        const message = getSetting('stripeErrorMessages').payment_unavailable;
        return (
            <StoreNotice
                status={'warning'}
                isDismissible={false}>
                {message.replace('%s', loadError.message)}
            </StoreNotice>
        )
    }

    return (
        <PaymentElement
            options={PAYMENT_ELEMENT_OPTIONS}
            onChange={onChange}
            onLoadError={onLoadError}/>
    )
}